import { Button, Typography } from "@blofin/blofin-ui";
import { useTranslations } from "use-intl";
import { FC, Fragment } from "react";
import DownloadDark from "@/nav-localized/assets/footer/social/download-dark.svg?react";
import DownloadLight from "@/nav-localized/assets/footer/social/download-light.svg?react";
import {
  TextColor,
  TitleColor,
  WrapperStyles,
  footerBg,
  simpleFooterBg,
  simpleTextColor,
  simpleTextLine,
  footerContainerStyle
} from "./styles";

import { useStoreState } from "./store/useStore";
import FooterLogo from "@/nav-localized/FooterLogo";
import { footerText } from "@/nav-localized/config";
import useFooter, { FooterMainProps, FooterMenuProps } from "@/nav-localized/hooks/useFooter";
import Community from "@/nav-localized/Community";
import MenuBadge from "@/nav-localized/components/MenuBadge";

const Footer: FC<{ footerBgColor?: string; isSimple?: boolean; size?: "1280" | "1440" }> = ({
  footerBgColor,
  isSimple,
  size = "1280"
}) => {
  const t = useTranslations("footer");

  const { theme, localeLink, isLogin, userInfo } = useStoreState();

  const isSubAccount = userInfo?.account_type === "NORMAL_SUB_ACCOUNT";

  const { FOOTER_MAIN, FOOTER_MENU, simpleUrl } = useFooter();

  if (isSimple) {
    return (
      <footer className={`${simpleFooterBg({ theme })} ${footerBgColor}`}>
        <div className="w-full max-w-screen-xl px-0 flex items-center">
          {simpleUrl.map((item, index) => (
            <div key={item.title} className="flex items-center justify-start">
              {item.outbound ? (
                <li className="leading-5">
                  <p className={`${simpleTextColor({ theme })}`}>
                    <a
                      href={`${item.link}`}
                      className="flex items-center"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t(item.title)}
                    </a>
                  </p>
                </li>
              ) : (
                <li className="leading-5">
                  <p className={`${simpleTextColor({ theme })}`}>
                    <a href={`${localeLink}${item.link}`} className="flex items-center">
                      {t(item.title)}
                    </a>
                  </p>
                </li>
              )}
              {index !== simpleUrl.length - 1 && (
                <div className={`${simpleTextLine({ theme })}`}></div>
              )}
            </div>
          ))}
          <div
            className={`ml-[48px] text-sm leading-[18px] ${theme === "light" ? "text-light-label-40" : "text-dark-label-40"}`}
          >
            {t("bbbbbbRights", { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`${footerBg({ theme })} ${footerBgColor}`}>
      <div className={`${footerContainerStyle({ theme, size })} px-0 md:px-[40px] mx-auto`}>
        <div className="mb-[40px] w-full">
          <Typography variant="body4" className={TitleColor({ theme })}>
            <FooterLogo theme={theme} />
          </Typography>
          <p
            className={`mt-[16px] ${TitleColor({
              theme
            })}`}
          >
            {footerText}
          </p>
        </div>
        <div className="flex flex-wrap items-start justify-between w-full gap-4 md:gap-0">
          <div className={WrapperStyles({ theme, isSubAccount })}>
            <div className="flex flex-wrap">
              {FOOTER_MAIN.map((row) => (
                <div key={row.label} className="md:basis-1/5 mb-2 md:mb-0">
                  <Typography
                    variant="subtitle2"
                    weight="medium"
                    className={`mb-[20px] ${TitleColor({ theme })}`}
                  >
                    {t(row.label)}
                  </Typography>
                  <ul className="flex flex-col gap-3">
                    {row.menus
                      .filter(
                        (menu: FooterMainProps["menus"][number]) =>
                          !(isSubAccount && menu?.isSupportSubAccount === false)
                      )
                      .map((menu) => (
                        <Fragment key={menu.title}>
                          {menu.outbound ? (
                            <li className="leading-5">
                              <p className="text-base font-normal">
                                <a
                                  href={`${menu.link}`}
                                  className="flex items-center"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {t(menu.title)}
                                  {(menu.extraBadges ?? []).map((badge, i) => (
                                    <MenuBadge key={i} text={badge} className="ml-[4px]" />
                                  ))}
                                </a>
                              </p>
                            </li>
                          ) : (
                            <li className="leading-5">
                              <p className="text-base font-normal">
                                <a
                                  href={`${localeLink}${menu.link}`}
                                  className="flex items-center"
                                  onClick={() => {
                                    if (menu.title === "buyCrypto") {
                                      try {
                                        localStorage.setItem("buy_crypto_page_source", "10");
                                      } catch {
                                        /* empty */
                                      }
                                    }
                                  }}
                                >
                                  {t(menu.title)}
                                </a>
                              </p>
                            </li>
                          )}
                        </Fragment>
                      ))}
                  </ul>
                </div>
              ))}
              <div className="md:basis-1/5 mb-2 md:mb-0">
                {FOOTER_MENU.map((row) => (
                  <div key={row.label} className="mb-[40px]">
                    <Typography
                      variant="subtitle2"
                      weight="medium"
                      className={`mb-[20px] ${TitleColor({ theme })}`}
                    >
                      {t(row.label)}
                    </Typography>
                    <ul className="flex flex-col gap-3">
                      {row.menus
                        .filter(
                          (menu: FooterMenuProps["menus"][number]) =>
                            !(isSubAccount && menu.isSupportSubAccount === false)
                        )
                        .map((menu: FooterMenuProps["menus"][number]) => (
                          <Fragment key={menu.title}>
                            {menu.outbound ? (
                              <li className="leading-5">
                                <p className="text-base font-normal">
                                  <a
                                    href={`${menu.link}`}
                                    className="flex items-center"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {t(menu.title)}
                                    {(menu.extraBadges ?? []).map((badge, i) => (
                                      <MenuBadge key={i} text={badge} className="ml-[4px]" />
                                    ))}
                                  </a>
                                </p>
                              </li>
                            ) : (
                              <li className="leading-5">
                                <p className="text-base font-normal">
                                  <a
                                    href={`${localeLink}${menu.link}`}
                                    className="flex items-center"
                                  >
                                    {t(menu.title)}
                                    {(menu.extraBadges ?? []).map((badge, i) => (
                                      <MenuBadge key={i} text={badge} className="ml-[4px]" />
                                    ))}
                                  </a>
                                </p>
                              </li>
                            )}
                          </Fragment>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="md:basis-1/5 mb-2 md:mb-0">
                <Typography
                  variant="subtitle2"
                  weight="medium"
                  className={`mb-[24px] ${TitleColor({ theme })}`}
                >
                  {t("tradeAnywhereWithbbbbbb")}
                </Typography>
                <Button
                  className="w-full mb-[32px]"
                  variant="tertiary"
                  size="large"
                  onClick={() => {
                    if (!isLogin) {
                      let referral_code = "";
                      if ("URLSearchParams" in window) {
                        referral_code =
                          new URLSearchParams(window.location.search).get("referral_code") || "";
                      }
                      window.location.href = `${localeLink}/register${referral_code ? `?referral_code=${referral_code}` : ""}`;
                    } else {
                      window.location.href = `/futures${localeLink}/BTC-USDT`;
                    }
                  }}
                >
                  {isLogin ? t("trade") : t("register")}
                </Button>
                <div>
                  {theme === "light" ? (
                    <DownloadLight className="w-[120px] h-[120px] mb-[12px]"></DownloadLight>
                  ) : (
                    <DownloadDark className="w-[120px] h-[120px] mb-[12px]"></DownloadDark>
                  )}
                </div>
                <span className={`${TextColor({ theme })} ml-[6px]`}>{t("download")}</span>
              </div>
            </div>
          </div>
          {/* NOTE:暂时隐藏社媒链接，因为社媒链接在移动端APP跳转时有问题（tg、邮箱） */}
          <div className="max-md:invisible">
            <Community />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
